import { IPaginatedResponse } from './api';
import { IMicrosoftTeamsChannel } from './msTeams';
import { ISlackChannel, ISlackUser, ISlackUserGroup } from './slack';
import { IUser } from './user';

export enum ChatContextType {
  MEETING = 0,
  CALL = 0,
  DEAL = 1,
  GLOBAL = 2,
  COMPANY = 3
}

export enum EScheduledPromptState {
  ARCHIVED = 0,
  PAUSED = 1,
  ACTIVE = 2
}

export enum EScheduledPromptTypes {
  PRIVATE = 0,
  ORGANIZATION = 1,
  DEFAULT = 2
}

export interface IScheduledPromptSchedule {
  uuid: string;
  /**
   * RRULE string for frequency schedules; use `''` for meeting-end triggers when the property is present.
   */
  rrule?: string;
  /**
   * Not sent on create/update (FE-only `scheduleKind` state). Optional on read for legacy;
   * otherwise infer meeting-end when `rrule` is absent or empty.
   */
  scheduleKind?: 'frequency' | 'meeting_end';
  state: EScheduledPromptState;
  unreadChatsCount: number;
  created: string;
  modified: string;
  emailNotificationSettings: {
    user: string[];
    team: string[];
  };
  lastRan?: string;
  msTeamsChannels: IMicrosoftTeamsChannel[];
  slackChannels: ISlackChannel[];
  slackUserGroups: ISlackUserGroup[];
  slackUsers: ISlackUser[];
  /** Present when this schedule is driven by meeting-end automation rules. */
  automationUuid?: string;
}

// Response format from BE (camelized) - contains UUIDs only
export interface INotificationTargets {
  slack?: {
    slackChannelUuids?: string[];
    slackUserUuids?: string[];
    slackUserGroupUuids?: string[];
  };
  msTeams?: {
    msTeamsChannelUuids?: string[];
  };
  email?: {
    users: IAlertUser[];
    teams: IAlertTeam[];
  };
}

interface IAlertUser {
  user?: {
    email?: string;
  };
  email?: string;
  uuid?: string;
}

interface IAlertTeam {
  uuid: string;
  name: string;
}

export interface IPromptCollection {
  uuid: string;
  name: string;
  description?: string;
  icon?: string;
  defaultId?: string;
  user?: IUser;
}

export interface IScheduledPrompt {
  uuid: string;
  text: string;
  contextType: ChatContextType;
  scheduledPrompts: IScheduledPromptSchedule[];
  scheduledUsers: IUser[];
  user: IUser;
  created: string;
  modified: string;
  name: string;
  promptType: EScheduledPromptTypes;
  collection?: IPromptCollection | null;
  defaultId?: string;
  lastUsed?: string;
  /** Meeting-end schedules backed by `/api/v1/automation/` (may be set when `scheduledPrompts` is empty). */
  automationUuid?: string;
}

export interface IChatContext {
  uuid: string | null;
  name: string;
  contextUuid: string | null;
  contextType: ChatContextType;
  label?: string;
  scheduledPromptUuid?: string | null;
  scheduledPromptName?: string | null;
}

/** Passed to Copilot "new chat" after deleting the active thread so entity scope is preserved. */
export type ICopilotCreateNewChatOptions = {
  preserveEntityScope?: Pick<IChatContext, 'contextType' | 'contextUuid'>;
};

/**
 * Mention row category (mirrors Copilot context-mention picker keys). Used when building
 * `data_json.context_uuids` and `data_json.mentions` (`member_emails`, `team_uuids`) for the
 * Copilot WebSocket send payload.
 */
export type CopilotAdditionalContextCategoryKey =
  | 'meetings'
  | 'deals'
  | 'companies'
  | 'teams'
  | 'members'
  | 'playlists';

export type TCopilotAttachmentPreviewState =
  | 'pending'
  | 'ready'
  | 'failed'
  | 'not_applicable';

export interface ICopilotAttachment {
  uuid: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  created: string;
  /** Backend-counted units for the file, e.g. 12 (`itemUnit: 'pages'`). */
  itemCount?: number | null;
  /** Unit for {@link itemCount}: 'pages' | 'rows' | 'lines' | 'slides' | 'sheets'. */
  itemUnit?: string | null;
  previewState?: TCopilotAttachmentPreviewState | null;
  fileUrl?: string | null;
  previewUrl?: string | null;
}

/**
 * Client-only snapshot for rendering “Add context” pills above a user bubble; not sent on the wire.
 * Survives optimistic → server merge via {@link mergeCopilotChatDetailMessages}.
 */
export interface ICopilotStandaloneContextPillDisplay {
  id: string;
  display: string;
  /**
   * Optional disambiguator shown under {@link display} in the expanded chat-message
   * pill list — e.g. the meeting date when two pills share a subject (current upcoming
   * vs. last-completed-in-series).
   */
  secondary?: string;
  /**
   * Company rows: domain line (lowercase), aligned with {@link CompanyFilter} search results.
   * When set, domain is not duplicated inside {@link secondary}.
   */
  companyDomain?: string;
  /** Company rows: CRM record URL (opens in a new tab). */
  crmObjectUrl?: string;
  categoryKey?: CopilotAdditionalContextCategoryKey;
}

/** One row in `data_json.context_uuids.*` or `mentions.*` on the Copilot WebSocket payload. */
export interface ICopilotStreamContextAttachment {
  attachment_type?: 0 | 1;
  uuid: string;
  label?: string;
  /** Rich company/account rows may send a display name without `label`. */
  name?: string;
  domain?: string;
  object_url?: string;
  objectUrl?: string;
  link?: string;
  owner?: {
    name?: string;
    uuid?: string;
    email?: string | null;
  };
}

export interface ICopilotAdditionalContextMention {
  id: string;
  categoryKey: CopilotAdditionalContextCategoryKey;
  /** For `members`: email for `data_json.mentions.member_emails` when `id` is a uuid. */
  email?: string;
  /** 0 = mentioned (@ chip), 1 = attached Add-context pill (page scope uses 1). */
  attachmentType?: 0 | 1;
  /** Human-readable name for the wire attachment row. */
  label?: string;
  /**
   * For `companies`: optional fields copied onto `context_uuids.account_uuids` rows
   * (`domain`, `object_url`) when present.
   */
  companyDomain?: string;
  crmObjectUrl?: string;
}

export interface IContextMap {
  [key: string]: {
    uuid: string;
    type: 'meeting' | 'meeting_engagement' | 'email_engagement';
    metadata?: {
      timestamp: string;
    };
  };
}

export interface IThinkingMessage {
  message: string;
  timestamp: string;
}

/** Buckets mirrored on the wire in `data_json.context_uuids` (snake or camel). */
export interface ICopilotMessageDataJsonContextUuids {
  meetingUuids?: Array<string | ICopilotStreamContextAttachment>;
  meeting_uuids?: Array<string | ICopilotStreamContextAttachment>;
  dealUuids?: Array<string | ICopilotStreamContextAttachment>;
  deal_uuids?: Array<string | ICopilotStreamContextAttachment>;
  accountUuids?: Array<string | ICopilotStreamContextAttachment>;
  account_uuids?: Array<string | ICopilotStreamContextAttachment>;
  playlistUuids?: Array<string | ICopilotStreamContextAttachment>;
  playlist_uuids?: Array<string | ICopilotStreamContextAttachment>;
}

/** Ask Avoma send payload uses plain string arrays; persisted rows may still use attachment rows. */
export interface ICopilotMessageDataJsonMentions {
  memberEmails?: Array<string | ICopilotStreamContextAttachment>;
  member_emails?: Array<string | ICopilotStreamContextAttachment>;
  teamUuids?: Array<string | ICopilotStreamContextAttachment>;
  team_uuids?: Array<string | ICopilotStreamContextAttachment>;
}

interface ICopilotMessageDataJsonBase extends Record<string, unknown> {
  contextUuids?: ICopilotMessageDataJsonContextUuids;
  context_uuids?: ICopilotMessageDataJsonContextUuids;
  mentions?: ICopilotMessageDataJsonMentions;
  attachmentUuids?: string[];
  attachment_uuids?: string[];
  step?: number;
  total?: number;
  responseStatus?: string;
  streamRunUuid?: string;
}

export interface ICopilotContextMessageDataJson
  extends ICopilotMessageDataJsonBase {
  contextType: ChatContextType;
  contextUuid: string;
  contextMap: IContextMap;
}

export interface ICopilotCancelledMessageDataJson
  extends ICopilotMessageDataJsonBase {
  responseStatus: 'cancelled';
  streamRunUuid: string;
  contextType?: never;
  contextUuid?: never;
  contextMap?: never;
}

/**
 * One node in an agent builder chat draft as it actually lands in Redux (`message.dataJson.agent`,
 * post `camelize()` — the whole Copilot SSE/WS dispatch pipeline camelizes every payload before
 * storing it). The backend's own wire format uses `sub_type`/`source_uuid`/`target_uuid`
 * (snake_case) — this type is the camelized shape those become, matching `IAgentBackendNodeRow`'s
 * convention in `types/agentsBackend.ts`. The backend sends `config` as a plain object, but
 * `helpers/copilotAgentBuilderDraftIngestion.ts` stringifies it before `camelize` (which would
 * otherwise rewrite its keys), so here it is always a JSON *string* — parse it with
 * `components/Agents/agentBuilderDraft.ts`'s helpers before handing to
 * `buildFlowFromBackendAgent`/`buildAgentFlowListCardPreview`, and re-derive the snake_case wire
 * shape (object `config`) with the same module before resending in `data_json.agent`.
 */
export interface IAgentBuilderDraftNode {
  uuid: string;
  type: number;
  subType?: string;
  config: string;
}

export interface IAgentBuilderDraftEdge {
  sourceUuid: string;
  targetUuid: string;
  order: number;
}

/** The agent draft shape as read from `data_json.agent` on a `finished_successfully` message. */
export interface IAgentBuilderDraft {
  scope: number;
  name: string;
  description?: string;
  nodes: IAgentBuilderDraftNode[];
  edges: IAgentBuilderDraftEdge[];
}

/**
 * A `finished_successfully` message from an agent builder chat (`feature: 1`) may carry a draft.
 * Explicitly `never`s the context fields (mirroring `ICopilotCancelledMessageDataJson`) so
 * existing `message.dataJson?.contextMap`-style reads across the union keep narrowing to a
 * concrete type instead of falling through to `ICopilotMessageDataJsonBase`'s index signature.
 */
export interface ICopilotAgentBuilderMessageDataJson
  extends ICopilotMessageDataJsonBase {
  agent?: IAgentBuilderDraft;
  contextType?: never;
  contextUuid?: never;
  contextMap?: never;
}

export type TCopilotMessageDataJson =
  | ICopilotContextMessageDataJson
  | ICopilotCancelledMessageDataJson
  | ICopilotAgentBuilderMessageDataJson;

export interface IChatMessage {
  uuid?: string;
  clientRequestId?: string;
  copilotRunUuid?: string;
  created?: string;
  author: number;
  message: string;
  /** Popover-only context (not inlined in `message`); rendered as chips above the user bubble. */
  copilotStandaloneContextPills?: ICopilotStandaloneContextPillDisplay[];
  attachments?: ICopilotAttachment[];
  dataJson?: TCopilotMessageDataJson;
  status?: string;
  thinkingMessages?: IThinkingMessage[];
  thinkingStartTime?: string;
  thinkingEndTime?: string;
}

export interface ICopilotChatMessage extends IChatMessage {}

export interface ICopilotSelectedContextEntry {
  uuid: string;
  label?: string;
  addedAt?: string;
  /** When `true`, the user removed it from the bar; do not restore it on load. */
  hidden: boolean;
}

/**
 * Chat-level persisted context bar. Bucketed dict mirroring `data_json.context_uuids`
 * (camelized from `selected_context`). Render entries where `hidden === false`.
 */
export interface ICopilotSelectedContext {
  meetingUuids?: ICopilotSelectedContextEntry[];
  dealUuids?: ICopilotSelectedContextEntry[];
  accountUuids?: ICopilotSelectedContextEntry[];
  playlistUuids?: ICopilotSelectedContextEntry[];
}

export interface ICopilotSelectedMentionEntry {
  /** Team uuid (`team_uuids`) or member email (`member_emails`). */
  value: string;
  addedAt?: string;
  hidden: boolean;
}

export interface ICopilotSelectedMentions {
  teamUuids?: ICopilotSelectedMentionEntry[];
  memberEmails?: ICopilotSelectedMentionEntry[];
}

export interface IChatHistory {
  uuid: string;
  contextUuid: string;
  contextType: ChatContextType;
  name: string;
  created: string;
  /** Present on some unified chat list rows when `messages` are omitted (camelized API field). */
  modified?: string;
  /**
   * Timestamp of the most recent message in the thread (camelized `last_message_time` from
   * `GET /api/v1/copilot/chats/`). Preferred recency signal for the unified list, which omits
   * `messages`. May be absent on legacy rows.
   */
  lastMessageTime?: string;
  user: IUser;
  messages: IChatMessage[];
  state?: EScheduledPromptState;
  /** Present on unified-list rows for scheduled prompt *report* runs (camelized API field). */
  scheduledPromptUuid?: string | null;
  selectedContext?: ICopilotSelectedContext;
  selectedMentions?: ICopilotSelectedMentions;
  /** `1` for an agent builder chat; absent/undefined for an ordinary Ask Avoma chat. */
  feature?: number;
}

/** `GET /api/v1/copilot/chats/` list response (web/desktop paginated shape). */
export type ICopilotChatListResponse = IPaginatedResponse<IChatHistory>;

export interface ICopilotChatHistoryPagination {
  next: string | null;
  count: number;
  pageSize: number;
  initialLoaded: boolean;
  fetchingMore: boolean;
  error: string | null;
}

export interface IScheduledPromptChat {
  uuid: string;
  name: string;
  created: string;
  user: IUser;
  messages: IChatMessage[];
  contextType: ChatContextType;
  contextUuid: string | null;
  isRead: boolean;
}
